import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SelectorItem,
  SubItem,
} from '@app/component/selector/selector.component';
import { CreditOptionDto } from '@app/model/credit/credit-option.dto';
import { CurrencyCode } from '@app/model/currency';
import { RouteComponent } from '@app/route/route.component';
import { LoanOption } from '@app/route/shared/LoanOption';
import { CreditDecisionService } from '@app/service/credit-decision/credit-decision.service';
import { CreditService } from '@app/service/credit/credit.service';
import { HeaderService } from '@app/service/header/header.service';
import {
  AuthUrlResponse,
  DebtRegisterReport,
  NoUserService,
} from '@app/service/no-user-service/no-user.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { StateService } from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { LoggingService } from '@app/service/logging-service/logging.service';

@Component({
  selector: 'no-invoice-confirm',
  templateUrl: './no-invoice-confirm.component.html',
  styleUrls: ['../../page-shared.css', './no-invoice-confirm.component.scss'],
})
export class NoInvoiceConfirmComponent
  extends RouteComponent
  implements OnInit
{
  modalActive: boolean;
  ctaActive: boolean;

  public loading = true;
  public navigationInProgress: boolean;
  public fallbackOffer: CreditOptionDto;

  selectorItems: SelectorItem[];
  selectedItem: SelectorItem;
  selectedInstallmentOption: LoanOption = null;
  debtRegisterReport: DebtRegisterReport;

  public authInProgress = false;
  public authComplete = false;
  public loadingText = 'Venter på BankID-identifikasjon...';
  private scriveSignIsActive: boolean;
  public authError: boolean = false;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected creditService: CreditService,
    protected creditDecisionService: CreditDecisionService,
    protected queryParamsService: QueryParamsService,
    private noUserService: NoUserService,
    private valuePipe: ValuePipe,
    private loggingService: LoggingService
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    const success = this.queryParamsService.getQueryParams().success;
    const transaction_id =
      this.queryParamsService.getQueryParams().transaction_id;

    if (success) {
      if (success === 'true' && transaction_id) {
        this.loggingService.log(
          `Came back from Scrive with following params: ?transaction_id=${transaction_id}&success=${success}`
        );
        this.handleCallback();
        return;
      } else {
        this.authError = true;
      }
    }

    try {
      this.selectedInstallmentOption = this.state.loanOptions.find(
        (o) => o.termId === this.state.loanOfferId
      );

      this.debtRegisterReport =
        await this.noUserService.getDebtRegisterInformation();
      this.scriveSignIsActive = await this.noUserService.getScriveSignActive();
    } catch (err) {
      this.handleError(err);
    }

    this.prepareModalInformation();
    this.loading = false;
  }

  async handleCtaClick() {
    this.authInProgress = true;

    try {
      // Scrive signing step inactive, just proceed without
      if (!this.scriveSignIsActive) {
        await this.noUserService.commit();
        await this.navigate();
      } else {
        const returnUrl: string = window.location.href.split('&')[0];
        await this.noUserService
          .initSign(returnUrl)
          .then((resp: AuthUrlResponse) => {
            this.authError = false;

            this.loggingService.log(
              'Leaving checkout to enter Scrive Sign flow'
            );
            window.location.href = resp.accessUrl;
          });
      }
    } catch (err) {
      this.handleError(err);
      // navigate on error to avoid hanging in loading state
      this.navigate();
    }
  }

  async handleCallback(): Promise<void> {
    this.authInProgress = false;
    this.authComplete = true;
    this.loadingText = 'Vente...';
    try {
      await this.noUserService.sign();
      this.navigate();
    } catch (err) {
      this.handleError(err);
      // navigate on error to avoid hanging in loading state
      this.navigate();
    }
  }

  getFormattedInterest(interest: number): string {
    if (!interest) {
      interest = 0;
    }
    return (interest * 100).toFixed(2);
  }

  toggleModalSelectDetailsTab() {
    this.selectedItem = this.selectorItems[0];
    this.toggleModal();
  }

  toggleModalSelectTermsTab() {
    this.selectedItem = this.selectorItems[1];
    this.toggleModal();
  }

  toggleModal() {
    this.modalActive = !this.modalActive;
  }

  toggleCta() {
    this.ctaActive = !this.ctaActive;
  }

  getTermsString(activeLoanOption: LoanOption): string {
    if (this.state.paymentInformation.paymentMethod == 'INVOICE') {
      return (
        `Når du velger å betale med faktura, tilbyr vi deg muligheten til å betale ${
          activeLoanOption?.numberOfDaysUntilFinalDueDate ?? 14
        } dager ` +
        `fra datoen fakturaen ble generert. Dersom fakturaen ikke betales innen ${
          activeLoanOption?.numberOfDaysUntilFinalDueDate ?? 14
        } dager påløper purregebyr ` +
        'fn. inntil kr 35 NOK pluss forsinkelserenter (8 % + gjeldende styrningsrente).'
      );
    } else if (
      this.state.paymentInformation.paymentMethod == 'CREDIT' ||
      this.state.paymentInformation.paymentMethod == 'CREDIT_STATIC_AMOUNT'
    ) {
      return this.noUserService.buildLoanTermsString(
        this.noUserService.transformDtoTOVM(
          this.selectedInstallmentOption,
          this.toggleModal.bind(this)
        ),
        this.state.totalCheckoutValue
      );
    }
  }

  prepareModalInformation() {
    const activeLoanOption: LoanOption =
      this.selectedInstallmentOption !== null
        ? this.selectedInstallmentOption
        : this.state.loanOptions[0];

    this.selectorItems = [
      {
        tab: 'Beskrivelse',
        title: 'Sammendrag',
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `Totalbeløp inkl. mva: ${this.valuePipe.transform(
          this.state.totalCheckoutValue
        )}`,
      },
      {
        tab: 'Betalingsbetingelser',
        title: 'Vilkår for faktura',
        descriptions: [
          {
            text: this.getTermsString(activeLoanOption),
          },
        ],
        ...(!!activeLoanOption.loanTermsURL && {
          links: [
            {
              text: 'Kredittavtale',
              onClick: () =>
                this.openURLinNewWindow(activeLoanOption.loanTermsURL),
            },
            {
              text: 'Standardiserte europeiske opplysninger om Forbrukerkreditt (SEF-skjema)',
              onClick: () => this.openURLinNewWindow(activeLoanOption.secciURL),
            },
          ],
        }),
      },
    ];

    if (this.debtRegisterReport) {
      const subItems: SubItem[] =
        this.debtRegisterReport.debtRegisterInformation.map((debtInfo) => ({
          amount: {
            amount: debtInfo.totalBalance,
            currencyCode: CurrencyCode.NOK,
          },
          title: debtInfo.financialInstitutionName,
        }));

      const gjeldRegisterselectorItem: SelectorItem = {
        tab: 'Gjeldsregisteret',
        title: 'Dine data fra Gjeldsregisteret AS',
        descriptions: [
          {
            text: 'Gjeldsopplysningene er levert av Gjeldsregisteret AS. Oversikt over finansforetak som leverer gjelds-opplysninger til Gjeldsregisteret AS finner dere på gjeldsregisteret.com',
            subItems: subItems,
          },
        ],
        links: [
          {
            text: 'Les mer om Gjeldsregisteret AS',
            onClick: () =>
              this.openURLinNewWindow('https://www.gjeldsregisteret.com/'),
          },
        ],
      };

      this.selectorItems.push(gjeldRegisterselectorItem);
    }
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }
}
